import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Phone, Mail, ExternalLink, X, MapPin } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PublicFooter({ settings, showOnlyOnResults = false }) {
  const [selectedKontakt, setSelectedKontakt] = useState(null);
  const kontakteRef = useRef([]);
  const loadedRef = useRef(false);

  // Fetch administration contacts and open modal
  const handleKontaktClick = async () => {
    // Show loading state first
    setSelectedKontakt({ _loading: true });
    
    try {
      if (!loadedRef.current) {
        const res = await axios.get(`${API_URL}/api/public/administration-kontakte`);
        kontakteRef.current = res.data;
        loadedRef.current = true;
      }
      
      const contacts = kontakteRef.current;
      
      if (contacts.length === 1) {
        setSelectedKontakt(contacts[0]);
      } else if (contacts.length > 1) {
        setSelectedKontakt({ _isList: true, contacts });
      } else {
        setSelectedKontakt({ _isEmpty: true });
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setSelectedKontakt({ _isEmpty: true });
    }
  };

  const anmeldungUrl = settings?.anmeldung_url;

  return (
    <>
      {/* Dezenter Footer - am Ende des Contents */}
      <div className="mt-8 pb-6 flex items-center justify-center gap-6">
        {/* Contact Link - dezent */}
        <button
          data-testid="footer-kontakt-btn"
          onClick={handleKontaktClick}
          className="text-sm text-gray-400 hover:text-gray-600 transition-colors underline-offset-2 hover:underline"
        >
          Kontakt
        </button>

        {/* Anmeldung Link - dezent */}
        {anmeldungUrl && (
          <a
            data-testid="footer-anmeldung-link"
            href={anmeldungUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors underline-offset-2 hover:underline flex items-center gap-1"
          >
            Anmeldung
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Kontakt Overlay - styled like Teacher Overlay */}
      <AnimatePresence>
        {selectedKontakt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setSelectedKontakt(null)}
            />

            {/* Overlay Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden"
              data-testid="kontakt-modal"
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedKontakt(null)}
                className="absolute top-4 right-4 z-10 w-8 h-8 bg-black/10 hover:bg-black/20 rounded-full flex items-center justify-center transition-colors"
                data-testid="close-kontakt-modal"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>

              {selectedKontakt._loading ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="animate-pulse">Lade...</div>
                </div>
              ) : selectedKontakt._isEmpty ? (
                /* No contacts available */
                <div className="p-8 text-center text-gray-500">
                  <p>Keine Kontakte verfügbar.</p>
                </div>
              ) : selectedKontakt._isList ? (
                /* Multiple Contacts List */
                <div className="p-6">
                  <h3 className="font-bold text-xl text-gray-900 mb-4">Kontakt</h3>
                  <div className="space-y-3">
                    {selectedKontakt.contacts.map((kontakt, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedKontakt(kontakt)}
                        className="w-full flex items-center gap-4 p-4 bg-gray-50 hover:bg-gray-100 rounded-2xl transition-colors text-left"
                      >
                        {/* Photo */}
                        <div className="w-14 h-14 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                          {kontakt.foto_url ? (
                            <img
                              src={`${API_URL}${kontakt.foto_url}`}
                              alt={`${kontakt.vorname} ${kontakt.nachname}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-[#FFF1F2] text-[#cf2e2e] font-bold text-lg">
                              {kontakt.vorname?.charAt(0) || '?'}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {kontakt.vorname} {kontakt.nachname}
                          </p>
                          <p className="text-sm text-gray-500">Ansprechperson</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                /* Single Contact Detail - like Teacher Overlay */
                <>
                  {/* Header with gradient background */}
                  <div className="bg-gradient-to-br from-gray-100 to-gray-50 pt-8 pb-6 px-6 text-center">
                    {/* Photo */}
                    <div className="w-24 h-24 mx-auto rounded-full bg-white shadow-lg overflow-hidden mb-4">
                      {selectedKontakt.foto_url ? (
                        <img
                          src={`${API_URL}${selectedKontakt.foto_url}`}
                          alt={`${selectedKontakt.vorname} ${selectedKontakt.nachname}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center bg-[#FFF1F2] text-[#cf2e2e] font-bold text-3xl">${selectedKontakt.vorname?.charAt(0) || '?'}</div>`;
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-[#FFF1F2] text-[#cf2e2e] font-bold text-3xl">
                          {selectedKontakt.vorname?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    
                    {/* Name */}
                    <h3 className="font-bold text-xl text-gray-900">
                      {selectedKontakt.vorname} {selectedKontakt.nachname}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Ansprechperson</p>
                  </div>

                  {/* Contact Details */}
                  <div className="p-6 space-y-3">
                    {selectedKontakt.email && (
                      <a
                        href={`mailto:${selectedKontakt.email}`}
                        className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
                      >
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                          <Mail className="w-5 h-5 text-gray-400 group-hover:text-[#cf2e2e] transition-colors" />
                        </div>
                        <span className="text-gray-700 group-hover:text-[#cf2e2e] transition-colors">
                          {selectedKontakt.email}
                        </span>
                      </a>
                    )}
                    
                    {selectedKontakt.telefon && (
                      <a
                        href={`tel:${selectedKontakt.telefon.replace(/\s/g, '')}`}
                        className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
                      >
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                          <Phone className="w-5 h-5 text-gray-400 group-hover:text-[#cf2e2e] transition-colors" />
                        </div>
                        <span className="text-gray-700 group-hover:text-[#cf2e2e] transition-colors">
                          {selectedKontakt.telefon}
                        </span>
                      </a>
                    )}

                    {/* Back to list button if multiple contacts */}
                    {kontakteRef.current.length > 1 && (
                      <button
                        onClick={() => setSelectedKontakt({ _isList: true, contacts: kontakteRef.current })}
                        className="w-full mt-4 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        ← Alle Kontakte anzeigen
                      </button>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
